import { WithInvalidFieldsContext } from 'components/common-form/contexts/WithInvalidFieldsContext';
import { SecondaryMarketToolbar } from 'components/secondaryMarket/shared/toolbar/SecondaryMarketToolbar';
import type { SecondaryMarketKeys, SecondaryMarketKeysPrefix } from 'locales/secondaryMarketKeys';
import { FC, ReactNode } from 'react';
import type { SecondaryMarketCpOrderForm } from 'state/secondaryMarket/cpOrder/cpOrderForm/input/cpOrderInputForm.model';
import type { SecondaryMarketFormError } from 'state/secondaryMarket/shared/form/validationResults';
import type { BcsProductKey } from 'state/workspaces/secondary-market/quotes/secondaryMarketQuotes.model';
import type { HttpErrorKind } from 'utils/errors/errors';

import { ModifiedFieldsContext } from '../shared';
import { SecondaryFormTemplate } from '../shared/components/SecondaryFormTemplate';
import { TabFatalError } from '../shared/TabFatalError';
import { CpOrderFormContentSkeleton } from './cpOrderFormContent.skeleton';

type CpOrderFormProps = Readonly<{
  children: ReactNode;
  tabId: string;
  sidePanel: ReactNode;
  productName: string | undefined;
  ric: string | undefined;
  hasFatalError: boolean;
  isLoading: boolean;
  validationErrors: SecondaryMarketFormError<SecondaryMarketCpOrderForm>[];
  traderLabel: string | undefined;
  bcsProductKey: BcsProductKey | undefined;
  modifiedFields?: string[];
  canResetForm: boolean;
  errorKind?: HttpErrorKind;
  onResetForm?: () => void;
  onRetryProductFetch?: () => void;
  errorMessage?: string;
}>;

export const CpOrderForm: FC<CpOrderFormProps> = ({
  bcsProductKey,
  productName,
  traderLabel,
  hasFatalError,
  isLoading,
  validationErrors,
  canResetForm,
  children,
  sidePanel,
  modifiedFields,
  onResetForm,
  onRetryProductFetch,
  errorKind,
  errorMessage,
}) => {
  if (hasFatalError) {
    if (errorKind === 'Timeout') {
      return <TabFatalError onRetry={onRetryProductFetch} detailsKey="generic.timeout.message" />;
    } else if (errorKind === 'BadRequest') {
      return <TabFatalError details={errorMessage} />;
    }
    return <TabFatalError />;
  }

  return (
    <ModifiedFieldsContext.Provider value={modifiedFields}>
      <WithInvalidFieldsContext<SecondaryMarketCpOrderForm, SecondaryMarketKeys, SecondaryMarketKeysPrefix>
        validationErrors={validationErrors}
      >
        <SecondaryFormTemplate
          toolbar={
            <SecondaryMarketToolbar
              bcsProductKey={bcsProductKey}
              productName={productName}
              isLoading={isLoading}
              traderLabel={traderLabel}
              canResetForm={canResetForm}
              onResetForm={onResetForm}
              type="closePriceOrder"
            />
          }
          sidePanel={sidePanel}
          bigSidePanel={false}
        >
          {isLoading ? <CpOrderFormContentSkeleton /> : children}
        </SecondaryFormTemplate>
      </WithInvalidFieldsContext>
    </ModifiedFieldsContext.Provider>
  );
};

CpOrderForm.displayName = 'CpOrderForm';
